import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Map as MapIcon, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// (Aucune couche officielle injectée ici par défaut — le teaser sert
// uniquement de bandeau visuel sur la landing. Les couches sont activables
// depuis le LandingMapPreview ci-dessous.)

// Tour automatique des 18 capitales européennes couvertes (pan toutes les 4s)
// Ordre géographique pour des transitions visuellement fluides
const CITY_TOUR = [
  { name: 'Reykjavik',  flag: '🇮🇸', center: [64.1466, -21.9426], zoom: 9 },
  { name: 'Paris',      flag: '🇫🇷', center: [48.8566, 2.3522],   zoom: 9 },
  { name: 'Bruxelles',  flag: '🇧🇪', center: [50.8503, 4.3517],   zoom: 10 },
  { name: 'Amsterdam',  flag: '🇳🇱', center: [52.3676, 4.9041],   zoom: 10 },
  { name: 'Luxembourg', flag: '🇱🇺', center: [49.6116, 6.1319],   zoom: 11 },
  { name: 'Copenhague', flag: '🇩🇰', center: [55.6761, 12.5683],  zoom: 9 },
  { name: 'Dublin',     flag: '🇮🇪', center: [53.3498, -6.2603],  zoom: 9 },
  { name: 'Tallinn',    flag: '🇪🇪', center: [59.4370, 24.7536],  zoom: 9 },
  { name: 'Nicosie',    flag: '🇨🇾', center: [35.1856, 33.3823],  zoom: 10 },
  { name: 'Berlin',     flag: '🇩🇪', center: [52.5200, 13.4050],  zoom: 9 },
  { name: 'Prague',     flag: '🇨🇿', center: [50.0755, 14.4378],  zoom: 9 },
  { name: 'Bratislava', flag: '🇸🇰', center: [48.1486, 17.1077],  zoom: 9 },
  { name: 'Varsovie',   flag: '🇵🇱', center: [52.2297, 21.0122],  zoom: 9 },
  { name: 'Vienne',     flag: '🇦🇹', center: [48.2082, 16.3738],  zoom: 9 },
  { name: 'Berne',      flag: '🇨🇭', center: [46.9480, 7.4474],   zoom: 10 },
  { name: 'Rome',       flag: '🇮🇹', center: [41.9028, 12.4964],  zoom: 9 },
  { name: 'Madrid',     flag: '🇪🇸', center: [40.4168, -3.7038],  zoom: 9 },
  { name: 'Lisbonne',   flag: '🇵🇹', center: [38.7223, -9.1393],  zoom: 9 },
];
const TOUR_INTERVAL_MS = 4000;

/**
 * HeroMapTeaser
 * Mini carte interactive (lecture seule) pour le hero de la landing page.
 * - Centrée sur Paris avec couches DGAC + OpenAIP visibles
 * - Toutes interactions désactivées (zoom, pan, click)
 * - Cliquable → scroll vers `#try-map` (vraie carte interactive)
 */
export default function HeroMapTeaser({ scrollTargetId = 'try-map', label }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const isMapReadyRef = useRef(false);
  const [cityIndex, setCityIndex] = useState(0);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initial = CITY_TOUR[0];
    const map = L.map(containerRef.current, {
      center: initial.center,
      zoom: initial.zoom,
      zoomControl: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
      dragging: false,
      attributionControl: false,
      zoomSnap: 0.25,
    });
    mapRef.current = map;

    // On mobile (or in any layout where the container is briefly 0x0),
    // Leaflet's internal projection produces NaN coordinates. We mark the
    // map as "ready" only once the container actually has dimensions,
    // and we recompute its size to avoid the (NaN, NaN) crash on flyTo.
    const markReady = () => {
      try {
        map.invalidateSize();
        const size = map.getSize();
        if (size && size.x > 0 && size.y > 0) {
          isMapReadyRef.current = true;
        }
      } catch (_) { /* noop */ }
    };
    // Defer to next frame so the DOM has its real layout
    const rafId = requestAnimationFrame(() => {
      markReady();
      // Second pass after a short delay for slow mobile layouts
      setTimeout(markReady, 250);
    });
    map.whenReady(markReady);

    // Fond OpenStreetMap — pas de couche métier en surimpression : nous voulons
    // un teaser minimal (le label "Carte interactive en direct" suffit à
    // communiquer la valeur produit, et l'utilisateur ouvre les couches lui-même
    // depuis le panneau "Couches" du LandingMapPreview ci-dessous).
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    return () => {
      cancelAnimationFrame(rafId);
      isMapReadyRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Tour automatique : pan toutes les 4 secondes vers la ville suivante
  useEffect(() => {
    const interval = setInterval(() => {
      setCityIndex((prev) => (prev + 1) % CITY_TOUR.length);
    }, TOUR_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Synchroniser la carte avec la ville courante via flyTo (animation smooth)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Skip the very first effect call: map was just initialized on this city
    if (cityIndex === 0 && !isMapReadyRef.current) return;
    // If the map isn't yet sized (mobile cold start), schedule a retry with setView
    const city = CITY_TOUR[cityIndex];
    try {
      map.invalidateSize();
      const size = map.getSize();
      if (!size || size.x <= 0 || size.y <= 0 || !isMapReadyRef.current) {
        // Try a non-animated setView (cheap, won't compute NaN bounds)
        try { map.setView(city.center, city.zoom, { animate: false }); } catch (_) {}
        return;
      }
      map.flyTo(city.center, city.zoom, {
        animate: true,
        duration: 2.5,
        easeLinearity: 0.25,
      });
    } catch (e) {
      // Ultimate fallback: jump without animation, swallow any further error
      try { map.setView(city.center, city.zoom, { animate: false }); } catch (_) {}
    }
  }, [cityIndex]);

  const handleClick = (e) => {
    e.preventDefault();
    const target = document.getElementById(scrollTargetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <a
      href={`#${scrollTargetId}`}
      onClick={handleClick}
      className="group relative block cursor-pointer"
      data-testid="hero-map-teaser"
      aria-label="Voir la carte interactive complète"
    >
      {/* Glow effect derrière la carte */}
      <div className="absolute -inset-4 bg-sky-500/30 rounded-2xl blur-2xl group-hover:bg-sky-400/40 transition-all duration-500"></div>

      {/* Conteneur de la carte */}
      <div className="relative rounded-2xl overflow-hidden border-2 border-sky-400/40 shadow-2xl group-hover:border-sky-300/60 transition-all duration-300">
        <div
          ref={containerRef}
          className="w-full"
          style={{ height: '440px', background: '#1e293b' }}
        />

        {/* Overlay coin haut-gauche : badge "Live" avec ville courante */}
        <div className="absolute top-3 left-3 z-[500] flex items-center gap-2 bg-slate-900/85 backdrop-blur-sm px-3 py-1.5 rounded-full border border-sky-400/40">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-xs font-semibold text-white tracking-wide flex items-center gap-1.5" data-testid="hero-map-current-city">
            <span className="text-sm leading-none">{CITY_TOUR[cityIndex].flag}</span>
            <span>{label || `${CITY_TOUR[cityIndex].name} · live`}</span>
          </span>
        </div>

        {/* Overlay coin haut-droit : icône carte + indicateurs de progression du tour */}
        <div className="absolute top-3 right-3 z-[500] flex items-center gap-2 bg-slate-900/85 backdrop-blur-sm px-2.5 py-2 rounded-lg border border-sky-400/40">
          <div className="flex items-center gap-0.5" data-testid="hero-map-tour-dots">
            {CITY_TOUR.map((city, idx) => (
              <span
                key={city.name}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  idx === cityIndex
                    ? 'w-3 bg-sky-400'
                    : 'w-1 bg-slate-500/60'
                }`}
              />
            ))}
          </div>
          <MapIcon className="w-4 h-4 text-sky-300" />
        </div>

        {/* Overlay bas : CTA "Essayer la carte" */}
        <div className="absolute bottom-0 left-0 right-0 z-[500] bg-gradient-to-t from-slate-900/95 via-slate-900/70 to-transparent p-4 pt-12">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-white font-semibold text-sm font-[Barlow]">
                {t('heroTeaser.title', 'Carte interactive en direct')}
              </div>
              <div className="text-slate-300 text-xs mt-0.5">
                {t('heroTeaser.subtitle', 'Cliquez pour explorer & dessiner votre zone')}
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-1.5 bg-sky-500 group-hover:bg-sky-400 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-lg transition-all duration-200 group-hover:scale-105">
              <span>{t('heroTeaser.cta', 'Essayer')}</span>
              <ArrowDown className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}
